import { getToolName, isToolUIPart, type UIMessage } from "ai";
import {
  buildTemplateStructure,
  parseStructureJson as parseStructureJsonText,
  structureToJsonString as structureToJsonStringFromSections,
} from "@/components/draft-templates/draft-template-sections";

export type DraftTemplateProposalInput = {
  name?: string;
  value?: string;
  blurb?: string;
  version?: string;
  structure?: unknown;
  id?: string;
};

export type ToolCallApprovalPayload = {
  runId: string;
  toolCallId: string;
  toolName: string;
  args: DraftTemplateProposalInput;
};

export type DraftTemplateToolOutput = {
  success?: boolean;
  draftTemplate?: {
    id: string;
    name: string;
    value: string;
    blurb?: string | null;
    version?: string;
    structure?: unknown;
  };
  error?: string;
};

export const WRITE_TOOL_NAMES = new Set([
  "createDraftTemplate",
  "updateDraftTemplate",
  "create-draft-template",
  "update-draft-template",
]);

export function isWriteToolName(toolName: string): boolean {
  return WRITE_TOOL_NAMES.has(toolName);
}

export function findSuccessfulDraftTemplateWrite(
  messages: UIMessage[]
): { toolCallId: string; draftTemplateId: string; name: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "assistant") continue;
    for (let j = message.parts.length - 1; j >= 0; j--) {
      const part = message.parts[j];
      if (!isToolUIPart(part)) continue;
      if (!isWriteToolName(getToolName(part))) continue;
      if (part.state !== "output-available") continue;
      const output = part.output as DraftTemplateToolOutput | undefined;
      if (output?.success !== true || !output.draftTemplate?.id) continue;
      return {
        toolCallId: part.toolCallId,
        draftTemplateId: output.draftTemplate.id,
        name: output.draftTemplate.name ?? "",
      };
    }
  }
  return null;
}

export function normalizeProposalInput(raw: unknown): DraftTemplateProposalInput {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  return {
    id: typeof o.id === "string" ? o.id : undefined,
    name: typeof o.name === "string" ? o.name : undefined,
    value: typeof o.value === "string" ? o.value : undefined,
    blurb: typeof o.blurb === "string" ? o.blurb : undefined,
    version: typeof o.version === "string" ? o.version : undefined,
    structure: o.structure,
  };
}

export function structureToJsonString(structure: unknown): string {
  return structureToJsonStringFromSections(structure);
}

/** Parses a JSON string into a template structure object (legacy assistant edit paths). */
export function parseStructureJson(text: string): unknown {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === "object" && Array.isArray((parsed as { sections?: unknown }).sections)) {
      return parsed;
    }
  } catch {
    // fall through to section list parse
  }
  return buildTemplateStructure(parseStructureJsonText(text));
}
